import { useContext, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { InFieldUser } from '@cognite/apm-client';

import { QueryKeys } from '../../../../utils/queryKeys';

import { UseCurrentUserQueryContext } from './cdm-use-current-user-query.context';

export const useCurrentUserQuery = () => {
  const { useAuthContext, useUserServices, useRegisterCurrentUser, Metrics } =
    useContext(UseCurrentUserQueryContext);

  const { mutateAsync: registerUser } = useRegisterCurrentUser();
  const { authState } = useAuthContext();
  const { userService } = useUserServices();

  const query = useQuery<InFieldUser | null, Error>({
    queryKey: [QueryKeys.USER],
    queryFn: async (): Promise<InFieldUser | null> => {
      const existingUser = await userService.getCurrentUser(
        authState.user.cdfId
      );

      const user: InFieldUser | null =
        existingUser === null
          ? await registerUser()
          : {
              externalId: authState.user.cdfId,
              name: authState.user.name,
              email: authState.user.email,
              preferences: existingUser.preferences,
            };

      return user;
    },
    meta: {
      error: { captureError: true, level: 'fatal' },
      metrics: { kind: ['sli', 'analytics'] },
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data } = query;

  useEffect(() => {
    const location = data?.preferences?.infield?.location;
    if (location) {
      Metrics.register({
        selectedLocationExternalId: location.externalId,
        selectedLocationSpace: location.space,
        selectedLocationName: location.name,
        project: authState.projectInfo?.id,
      });
    }
  }, [data, authState.projectInfo]);

  return {
    data: query.data,
    isError: query.isError,
    error: query.error,
  };
};
